import React from 'react'
import { observer } from 'mobx-react'
import {
  ModalSheet,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
  ModalSheetTitle,
} from 'components/ModalSheet'
import { BulkDuplicateBundleViewModel } from './BulkDuplicateBundleViewModel'
import Button from '../../../../components/Button'
import BundleFormWorkspaceDropdown from '../../components/BundleFormWorkpaceDropdown'
import styles from './BulkDuplicateBundleDIalog.sass'
import Tooltip from '../../../../components/Tooltip'
import Icon from '../../../../components/Icon'
import { Bundle, BundleIncludedItem } from '../../bundle'

interface Props {
  vm: BulkDuplicateBundleViewModel
}

const BundleItemCompatibility = ({
  bundle,
  compatibility,
  items,
}: {
  bundle: Bundle
  compatibility: string
  items?: BundleIncludedItem[]
}) => {
  const isPartial = items && items.length > 0
  const toolTipContent = isPartial
    ? `These items will be excluded: ${items?.map((item) => item.name).join(', ')}`
    : `The base item is incompatible: ${bundle.baseItem.name}`

  return (
    <div className={styles.itemContainer}>
      <div className={styles.itemTextContainer}>
        <div className={styles.title}>{bundle.name}</div>
        <div className={styles.description}>{bundle.description}</div>
      </div>

      <span className={styles.itemCompatibility}>
        {compatibility}

        <Tooltip content={toolTipContent}>
          <Icon
            name="info"
            size={18}
            color={isPartial ? 'var(--grey700)' : 'var(--red500'}
          />
        </Tooltip>
      </span>
    </div>
  )
}

const BulkDuplicateBundleDialog = ({ vm }: Props) => {
  return (
    <ModalSheet showing={vm.showing} onRequestClose={vm.close}>
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>
          Duplicate selected bundles ({vm.selectedBundles.length})
        </ModalSheetTitle>
      </ModalSheetHeader>

      <ModalSheetSection spaced>
        <BundleFormWorkspaceDropdown
          vm={vm}
          workspaceSelectorVM={vm.workspaceSelectorVM}
          label="Target workspace"
        />
      </ModalSheetSection>

      {(vm.incompatibleBundleList.length > 0 ||
        vm.partiallyCompatibleBundleList.length > 0) && (
        <>
          <div className={styles.compatibilityHeader}>
            <span>Bundle</span>

            <span className={styles.itemCompatibility}>
              Compatibility
              <Tooltip
                content={
                  "Incompatible Bundles can't be duplicated, Partial means it would be duplicated without some service items"
                }
                placement="top"
              >
                <Icon name="info" size={18} color="var(--blue400)" />
              </Tooltip>
            </span>
          </div>

          <div className={styles.itemListContainer}>
            {vm.incompatibleBundleList.map((bundle) => (
              <BundleItemCompatibility
                key={bundle.Id}
                bundle={bundle}
                compatibility="Incompatible"
              />
            ))}
            {vm.partiallyCompatibleBundleList.map((bundle) => (
              <BundleItemCompatibility
                key={bundle.Id}
                bundle={bundle}
                compatibility="Partial"
                items={vm.getIncompatibleItems(bundle)}
              />
            ))}
          </div>
        </>
      )}

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button type="default" onClick={vm.close}>
            Close
          </Button>

          <Button
            type="primary"
            data-test="user-management-remove-from-workspace-remove-button"
            onClick={vm.submit}
            disabled={vm.submit.pending || vm.compatibleBundles.length === 0}
            loading={vm.submit.pending}
          >
            Duplicate{' '}
            {vm.compatibleBundles.length ? vm.compatibleBundles.length : ''}
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

export default observer(BulkDuplicateBundleDialog)
